/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Select } from 'antd'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  paragraphSmall,
  paragraphXSmall,
  weightRegular,
} from 'src/components/css'

export interface SearchFieldOption {
  value: string
  label: string
}

interface Props {
  label: string
  value: string | null
  options: SearchFieldOption[]
  onChange: (value: string | null) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  loading?: boolean
  hasError?: boolean
  /** API error message, rendered under the field. */
  error?: string
  /** Rendered above the option list — used for the "All Locations" toggle. */
  header?: React.ReactNode
  /** Rendered under the field — used for the "Create a new location" row. */
  footer?: React.ReactNode
  notFoundContent?: React.ReactNode
}

// Above this modal (9999) but below the nested LocationModal's mask (10000).
const dropdownStyle: React.CSSProperties = { zIndex: 9999 }

/**
 * antd invokes `dropdownRender` as a plain function -- not as a component --
 * so the returned tree is never remounted between renders. Built at module
 * scope so the arrow is not read as a component defined during render.
 */
const buildDropdownRender =
  (header: React.ReactNode, footer: React.ReactNode) =>
  (menu: React.ReactElement) =>
    (
      <>
        {header && <div css={headerStyle}>{header}</div>}
        {menu}
        {footer && <div css={footerStyle}>{footer}</div>}
      </>
    )

/**
 * A searchable single-select for the team / location / facility pickers.
 *
 * The legacy `SearchBox` filtered a preloaded array client-side and opened its
 * list on focus; this keeps that behavior (all options are loaded up front and
 * `filterOption` matches on the label) while wearing the same 40px chrome as
 * the rest of the form. The legacy magnifier button was inert -- it had
 * `pointer-events: none` -- so it is not reproduced.
 */
const GameSearchField: React.FC<Props> = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  loading = false,
  hasError = false,
  error,
  header,
  footer,
  notFoundContent,
}) => (
  <div css={fieldStyle}>
    <span css={labelStyle}>
      {capitalizeWords(label)}
      {required && <span css={requiredMarkStyle}> *</span>}
    </span>
    <Select
      showSearch
      allowClear
      value={value ?? undefined}
      onChange={(next) => onChange(next ?? null)}
      options={options}
      disabled={disabled}
      loading={loading}
      status={hasError || error ? 'error' : ''}
      //  Title-cased here rather than at each call site, matching how the
      //  label above is handled -- several of these come from locale values
      //  that are lower case ("Select a sports complex").
      placeholder={placeholder ? capitalizeWords(placeholder) : placeholder}
      optionFilterProp='label'
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      notFoundContent={notFoundContent}
      css={selectStyle}
      // Anchored to the field so the list travels with the modal rather than
      // staying put when the body scrolls.
      getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
      dropdownStyle={dropdownStyle}
      dropdownRender={
        header || footer ? buildDropdownRender(header, footer) : undefined
      }
    />
    {error && <span css={errorStyle}>{error}</span>}
  </div>
)

export default GameSearchField

// ─── Styles ────────────────────────────────────────────────────────────────

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const labelStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.SOLITUDE};
  min-height: 20px;
`

const requiredMarkStyle = css`
  color: ${colors.WHITE};
`

const selectStyle = css`
  width: 100%;

  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    border: 1px solid ${colors.SECONDARY_100} !important;
    background: ${colors.SECONDARY_200} !important;
  }

  /*
   * The rule above sets the border with !important, which beats antd's own
   * error styling -- so the error status alone left the field looking normal
   * while its message sat in red underneath. Restate it at the same weight.
   * (No backticks in here: this is inside a template literal.)
   */
  &.ant-select-status-error .ant-select-selector {
    border-color: ${colors.ERROR_LIGHT} !important;
  }

  .ant-select-selection-search-input {
    height: 38px !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: 38px !important;
  }

  .ant-select-selection-item {
    color: ${colors.WHITE};
  }
`

const errorStyle = css`
  ${paragraphXSmall}
  color: ${colors.ERROR_LIGHT};
`

const headerStyle = css`
  border-bottom: 1px solid ${colors.SECONDARY_100};
  padding: 4px 4px 8px;
`

const footerStyle = css`
  border-top: 1px solid ${colors.SECONDARY_100};
  padding: 8px 4px 4px;
`
